import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SortIcon } from '@/components/shared/reports/SortIcon';
import { RotateCcw, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PostingLogRow, SortKey, SortDir } from '@/utils/PostingLog/types';
import { formatDate, formatCurrency } from '@/utils/PostingLog/formatters';
import { StatusBadge } from './StatusBadge';

interface LineTableProps {
  lines: PostingLogRow[];
  /** When present, renders the Timesheet ID as a clickable link */
  onTimesheetClick?: (timesheetId: string) => void;
  onRetry: (rowId: string) => void;
  retryingIds: Set<string>;
  disabledRetryIds: Map<string, string>;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  allFailedOnPageSelected?: boolean;
  onToggleSelectAll?: () => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort?: (key: SortKey) => void;
  allowRetry: boolean;
  compact?: boolean;
  columnVisibility?: Record<string, boolean>;
}

export const LineTable: React.FC<LineTableProps> = ({
  lines, onTimesheetClick, onRetry, retryingIds, disabledRetryIds,
  selectedIds, onToggleSelect, allFailedOnPageSelected, onToggleSelectAll,
  sortKey, sortDir, onSort, allowRetry, compact = false, columnVisibility,
}) => {
  const col = (id: string) => !columnVisibility || columnVisibility[id] !== false;
  const SortHeader: React.FC<{ col: SortKey; label: string; className?: string }> = ({ col: sortCol, label, className }) => (
    <TableHead
      className={cn('whitespace-nowrap', onSort && 'cursor-pointer select-none', className)}
      onClick={() => onSort?.(sortCol)}
    >
      {label}
      {onSort && <SortIcon col={sortCol} current={sortKey} dir={sortDir} />}
    </TableHead>
  );

  return (
    <div className="rounded-md border overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {/* Checkbox column */}
              {allowRetry && onToggleSelect && (
                <TableHead className="w-10 px-3">
                  <Checkbox
                    checked={allFailedOnPageSelected ?? false}
                    onCheckedChange={onToggleSelectAll}
                    aria-label="Select all failed rows on this page"
                  />
                </TableHead>
              )}

              {/* Timesheet ID col — only in main table */}
              {onTimesheetClick && col('timesheetId') && <TableHead className="whitespace-nowrap">Timesheet</TableHead>}

              {col('employeeName') && <SortHeader col="employeeName" label="Employee" />}
              {col('transactionDate') && <SortHeader col="transactionDate" label="Date" />}
              {col('projectName') && <SortHeader col="projectName" label="Project" />}
              {col('costCentreName') && <TableHead>Cost Centre</TableHead>}
              {col('quantity') && <SortHeader col="quantity" label="Hours" className="text-right" />}
              {col('price') && <TableHead className="text-right">Rate</TableHead>}
              {col('upliftValue') && <TableHead className="text-right">Uplift</TableHead>}
              {col('status') && <TableHead>Status</TableHead>}
              {col('externalTransactionId') && <TableHead className="whitespace-nowrap">ERP Ref</TableHead>}
              {col('errorMessage') && <TableHead className={compact ? 'max-w-[140px]' : 'max-w-[200px]'}>Error</TableHead>}
              {allowRetry && <TableHead className="w-20">Action</TableHead>}
            </TableRow>
          </TableHeader>

          <TableBody>
            {lines.map((row) => {
              const isFailed   = row.status === 'Failed';
              const isPosted   = row.status === 'Posted';
              const isRetrying = retryingIds.has(row.id);
              const disabledReason = disabledRetryIds.get(row.id);
              const isSelected = selectedIds?.has(row.id) ?? false;

              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    'transition-colors',
                    isFailed && 'border-l-2 border-l-error-500 bg-error-50/30 dark:bg-error-950/10',
                  )}
                >
                  {/* Checkbox */}
                  {allowRetry && onToggleSelect && (
                    <TableCell className="px-3">
                      {isFailed && (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onToggleSelect(row.id)}
                          disabled={isRetrying}
                          aria-label={`Select ${row.id}`}
                        />
                      )}
                    </TableCell>
                  )}

                  {/* Timesheet link */}
                  {onTimesheetClick && col('timesheetId') && (
                    <TableCell>
                      <button
                        type="button"
                        className="text-sm text-primary underline-offset-2 hover:underline font-medium"
                        onClick={() => onTimesheetClick(row.timesheetId)}
                      >
                        {row.timesheetId}
                      </button>
                    </TableCell>
                  )}

                  {col('employeeName') && <TableCell className="text-sm font-medium whitespace-nowrap">{row.employeeName}</TableCell>}
                  {col('transactionDate') && <TableCell className="text-sm whitespace-nowrap">{formatDate(row.transactionDate)}</TableCell>}
                  {col('projectName') && <TableCell className="text-sm">{row.projectName}</TableCell>}
                  {col('costCentreName') && <TableCell className="text-sm text-muted-foreground">{row.costCentreName}</TableCell>}
                  {col('quantity') && <TableCell className="text-sm text-right">{row.quantity.toFixed(2)}</TableCell>}
                  {col('price') && <TableCell className="text-sm text-right whitespace-nowrap">{formatCurrency(row.price)}</TableCell>}
                  {col('upliftValue') && (
                    <TableCell className="text-sm text-right whitespace-nowrap">
                      {row.upliftValue !== 0
                        ? formatCurrency(row.upliftValue)
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}

                  {/* Status */}
                  {col('status') && (
                    <TableCell>
                      {isRetrying
                        ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        : <StatusBadge status={row.status} />}
                    </TableCell>
                  )}

                  {/* ERP Ref */}
                  {col('externalTransactionId') && (
                    <TableCell className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                      {row.externalTransactionId ?? <span className="opacity-40">—</span>}
                    </TableCell>
                  )}

                  {/* Error (truncated + tooltip) */}
                  {col('errorMessage') && (
                    <TableCell className={compact ? 'max-w-[140px]' : 'max-w-[200px]'}>
                      {row.errorMessage ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-xs text-error-700 dark:text-error-400 truncate block cursor-help">
                              {row.errorMessage.length > 60
                                ? row.errorMessage.slice(0, 60) + '…'
                                : row.errorMessage}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">{row.errorMessage}</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground opacity-40 text-xs">—</span>
                      )}
                    </TableCell>
                  )}

                  {/* Action: Retry */}
                  {allowRetry && (
                    <TableCell>
                      {!isPosted && (
                        disabledReason ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" disabled>
                                  <RotateCcw className="h-3 w-3" />
                                  Retry
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{disabledReason}</TooltipContent>
                          </Tooltip>
                        ) : isFailed ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs gap-1"
                            onClick={() => onRetry(row.id)}
                            disabled={isRetrying}
                          >
                            {isRetrying
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : <RotateCcw className="h-3 w-3" />}
                            Retry
                          </Button>
                        ) : null
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
